import {
  getCurrentUser,
  getOrganizationById,
  getOrganizations, IGetCurrentUserResponse,

  IGetOrganizationByIdResponse,
  IGetOrganizationsResponse,
} from "@/auth/api/requests/user.requests.ts";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/auth/api/config.ts";
import { ApiResponse } from "@/shared/types/api.ts";
import { useSession } from "@/auth/contexts/auth-context.tsx";


export const useGetOrganizations = () => {
  const { accessToken, isLoading: authLoading } = useSession();

  return useQuery<ApiResponse<IGetOrganizationsResponse[]>>({
    queryKey: [QUERY_KEYS.USER_ORGANIZATION],
    queryFn: () => getOrganizations(),
    enabled: !!accessToken && !authLoading,
    staleTime: 1000 * 60 * 10, // 10 minutes - organizations change infrequently
    gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache
  });
};

export const useGetOrganizationById = (id: number) => {
  const { accessToken, isLoading: authLoading } = useSession();

  return useQuery<ApiResponse<IGetOrganizationByIdResponse>>({
    queryKey: [QUERY_KEYS.USER_ORGANIZATION, id],
    queryFn: () => getOrganizationById(id),
    enabled: !!id && !!accessToken && !authLoading,
    staleTime: 1000 * 60 * 10, // 10 minutes - organizations change infrequently
    gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache
  })
}

export const useGetUserMe = () => {
  const { accessToken, refreshToken, isLoading: authLoading } = useSession();

  // Additional safety check: verify localStorage has tokens even if state doesn't yet
  const hasStoredTokens = typeof window !== 'undefined' && (
    localStorage.getItem('maestro-access-token') !== null ||
    localStorage.getItem('maestro-refresh-token') !== null
  );

  // Query is enabled when:
  // 1. Auth context finished loading from localStorage
  // 2. We have tokens either in state OR in localStorage
  const isEnabled = !authLoading && (!!accessToken || !!refreshToken || hasStoredTokens);

  console.log('[useGetUserMe] Query state:', {
    authLoading,
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!refreshToken,
    hasStoredTokens,
    isEnabled,
  });

  return useQuery<ApiResponse<IGetCurrentUserResponse>>({
    queryKey: [QUERY_KEYS.USER_ME],
    queryFn: () => {
      console.log('[useGetUserMe] Executing query to /users/me/');
      const token = accessToken || localStorage.getItem('maestro-access-token');
      console.log('[useGetUserMe] Token available for request:', !!token);
      return getCurrentUser();
    },
    // Only run when auth is initialized and we have at least a refresh token
    enabled: isEnabled,
    retry: false,
    // Prevent automatic refetching since auth state is managed via interceptor
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })
}